/**
 * ============================================================================
 * EXPERIMENT: Calendly Integration (ab_calendly)
 * PR: #TODO_AFTER_PR_CREATION_ab_calendly
 *
 * Calendly OAuth token management via localStorage.
 * Temporary code — will be REMOVED when the experiment concludes.
 *
 * Find all experiment code: grep -r "ab_calendly" src/
 * ============================================================================
 */

import { CalendlyTokenResponse } from './AB_calendly-types';

const STORAGE_KEY = 'ab_calendly_token';
const listeners = new Set<() => void>();

function notifyListeners(): void {
    listeners.forEach((fn) => fn());
}

export function subscribeToTokenChanges(callback: () => void): () => void {
    listeners.add(callback);

    const onStorage = (e: StorageEvent): void => {
        if (e.key === STORAGE_KEY) callback();
    };
    window.addEventListener('storage', onStorage);

    return () => {
        listeners.delete(callback);
        window.removeEventListener('storage', onStorage);
    };
}
const CALENDLY_AUTH_URL = 'https://auth.calendly.com/oauth/authorize';
const CALENDLY_TOKEN_URL = 'https://auth.calendly.com/oauth/token';

// POC: client_id and client_secret are read from localStorage.
// Set them manually in DevTools before using the OAuth flow:
//   localStorage.setItem('ab_calendly_client_id', '...')
//   localStorage.setItem('ab_calendly_client_secret', '...')
function getClientId(): string {
    const id = localStorage.getItem('ab_calendly_client_id');
    if (!id) throw new Error('Missing ab_calendly_client_id in localStorage');
    return id;
}

function getClientSecret(): string {
    const secret = localStorage.getItem('ab_calendly_client_secret');
    if (!secret) throw new Error('Missing ab_calendly_client_secret in localStorage');
    return secret;
}

function getRedirectUri(): string {
    return `${window.location.origin}/request-manager/auth/calendly/callback`;
}

function getStoredToken(): CalendlyTokenResponse | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    try {
        const token: CalendlyTokenResponse = JSON.parse(raw);
        const expiresAt = (token.created_at + token.expires_in) * 1000;
        if (Date.now() > expiresAt) {
            return null;
        }
        return token;
    } catch {
        localStorage.removeItem(STORAGE_KEY);
        return null;
    }
}

function storeToken(token: CalendlyTokenResponse): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(token));
    notifyListeners();
}

export function clearToken(): void {
    localStorage.removeItem(STORAGE_KEY);
    notifyListeners();
}

export function getAccessToken(): string | null {
    return getStoredToken()?.access_token ?? null;
}

export function isConnected(): boolean {
    return getStoredToken() !== null;
}

export function initiateOAuth(): void {
    const params = new URLSearchParams({
        client_id: getClientId(),
        redirect_uri: getRedirectUri(),
        response_type: 'code',
    });

    window.location.href = `${CALENDLY_AUTH_URL}?${params.toString()}`;
}

export async function exchangeCodeForToken(code: string): Promise<CalendlyTokenResponse> {
    const response = await fetch(CALENDLY_TOKEN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            grant_type: 'authorization_code',
            client_id: getClientId(),
            client_secret: getClientSecret(),
            code,
            redirect_uri: getRedirectUri(),
        }),
    });

    if (!response.ok) {
        throw new Error(`Calendly token exchange failed: ${response.status}`);
    }

    const token: CalendlyTokenResponse = await response.json();
    storeToken(token);
    return token;
}
